/**
 * OfflineBanner — Sticky alert displayed when navigator.onLine is false.
 *
 * Mounts at the top of the chat surface (ChatShell center column) and
 * auto-dismisses when the browser's `online` event fires. Never manually
 * dismissable — the point is that it goes away on its own when the
 * connection recovers.
 *
 * Uses:
 *   - `useOnline()` from `@/lib/use-online` (Wave 2A prereq, already on master)
 *   - `Alert` / `AlertDescription` from `@/components/ui/alert`
 *
 * Spec: docs/CHAT-UI-IMPROVEMENT-DESIGN.md §3.6 Track 6 + §5 Wave 3B
 */

import { WifiOff } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useOnline } from '@/lib/use-online';

/**
 * Renders a destructive Alert banner when the browser reports offline.
 * Returns null when online so the component incurs zero DOM cost in the
 * happy path.
 *
 * Mount this ABOVE the chat message area in ChatShell so it is the first
 * thing a user sees when connectivity drops.
 */
export function OfflineBanner() {
  const online = useOnline();

  if (online) {
    return null;
  }

  return (
    <Alert
      variant="destructive"
      className="rounded-none border-x-0 border-t-0 border-b shrink-0"
      role="status"
      aria-live="assertive"
    >
      <WifiOff className="h-4 w-4" />
      <AlertDescription>
        You&apos;re offline. Reconnect to continue.
      </AlertDescription>
    </Alert>
  );
}
